import React, { useState } from 'react';
import { CustomField, RSVPPayload } from '@/types';

interface RSVPFormProps {
  customFields: CustomField[];
  onSubmit: (data: RSVPPayload) => Promise<void>;
  loading?: boolean;
  allowMaybe?: boolean;
}

export function RSVPForm({ customFields, onSubmit, loading = false, allowMaybe = true }: RSVPFormProps) {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    attending: 'yes' as 'yes' | 'no' | 'maybe',
    guest_count: 1,
    custom_responses: {} as Record<string, string>,
    comments: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCustomChange = (fieldId: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      custom_responses: { ...prev.custom_responses, [fieldId]: value },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="text-center py-12 animate-scale-in">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank you!</h3>
        <p className="text-gray-600">Your RSVP has been submitted successfully.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Full Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          required
          value={formData.full_name}
          onChange={(e) => handleChange('full_name', e.target.value)}
          className="input-field"
          placeholder="Your full name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          required
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          className="input-field"
          placeholder="your@email.com"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Will you attend? <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-3">
          {(['yes', 'no', ...(allowMaybe ? ['maybe'] : [])] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleChange('attending', option)}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium border-2 transition-all duration-200 ${
                formData.attending === option
                  ? 'border-violet-500 bg-violet-50 text-violet-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {option === 'yes' ? '✓ Yes' : option === 'no' ? '✕ No' : '? Maybe'}
            </button>
          ))}
        </div>
      </div>

      {formData.attending !== 'no' && (
        <div className="animate-slide-down">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Number of Guests (including yourself)
          </label>
          <input
            type="number"
            min={1}
            max={20}
            value={formData.guest_count}
            onChange={(e) => handleChange('guest_count', e.target.value === '' ? '' : parseInt(e.target.value) || '')}
            onBlur={(e) => { if (!e.target.value || parseInt(e.target.value) < 1) handleChange('guest_count', 1); }}
            className="input-field w-24"
          />
        </div>
      )}

      {/* Custom Fields */}
      {customFields
        .filter((field) => !(field.hideOnNo && formData.attending === 'no'))
        .map((field) => (
        <div key={field.id} className="animate-fade-in">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>

          {field.type === 'text' && (
            <input
              type="text"
              required={field.required}
              value={formData.custom_responses[field.id] || ''}
              onChange={(e) => handleCustomChange(field.id, e.target.value)}
              className="input-field"
            />
          )}

          {field.type === 'textarea' && (
            <textarea
              required={field.required}
              value={formData.custom_responses[field.id] || ''}
              onChange={(e) => handleCustomChange(field.id, e.target.value)}
              className="input-field min-h-[100px] resize-y"
              rows={3}
            />
          )}

          {field.type === 'dropdown' && (
            <select
              required={field.required}
              value={formData.custom_responses[field.id] || ''}
              onChange={(e) => handleCustomChange(field.id, e.target.value)}
              className="input-field"
            >
              <option value="">Select an option</option>
              {field.options?.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          )}

          {field.type === 'number' && (
            <input
              type="number"
              required={field.required}
              value={formData.custom_responses[field.id] || ''}
              onChange={(e) => handleCustomChange(field.id, e.target.value)}
              className="input-field w-32"
            />
          )}

          {field.type === 'checkbox' && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.custom_responses[field.id] === 'true'}
                onChange={(e) =>
                  handleCustomChange(field.id, e.target.checked ? 'true' : 'false')
                }
                className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              <span className="text-sm text-gray-600">{field.label}</span>
            </label>
          )}
        </div>
      ))}

      {/* Comments */}
      <div className="animate-fade-in">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Comments
        </label>
        <textarea
          value={formData.comments}
          onChange={(e) => handleChange('comments', e.target.value)}
          className="input-field min-h-[80px] resize-y"
          rows={3}
          placeholder="Any additional comments (optional)"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Submitting...
          </span>
        ) : (
          'Submit RSVP'
        )}
      </button>
    </form>
  );
}
